/**
 * ShareLockIllustration
 *
 * Decorative left-panel background for the public share PASSWORD gate.
 * Concept: "secured location" — map roads converging on a central pin whose
 * face holds a keyhole, ringed by concentric security perimeters. Mirrors the
 * visual language of OnboardingMapIllustration (grid + Bézier roads + pins) and
 * is fully theme-aware via useMapIllustrationTheme().
 *
 * @module components/share/public/shareGate/ShareLockIllustration
 */
import { memo } from 'react';

import { useMapIllustrationTheme } from '@/hooks/useMapIllustrationTheme';

import styles from './ShareLockIllustration.module.css';

/** Center of the secured location, in viewBox units. */
const CENTER = { x: 250, y: 410 };

/** Lock pin + keyhole geometry. */
const PIN_RADIUS = 40;
const KEYHOLE_CIRCLE_RADIUS = 12;
const KEYHOLE_CIRCLE_CY = 400;
const KEYHOLE_STEM = 'M243 408 L257 408 L262 438 L238 438 Z';

/** Concentric security rings around the center (radius + opacity factor). */
const INNER_RING_RADIUS = 78;
const PULSE_MAX_RADIUS = 200;
const SECURITY_RINGS = [
  { radius: INNER_RING_RADIUS, opacityFactor: 1 },
  { radius: 118, opacityFactor: 0.7 },
  { radius: 162, opacityFactor: 0.45 },
];

/** Roads converging toward the secured center. */
const CONVERGING_ROADS = [
  'M-20 110 Q120 250 250 410',
  'M520 90 Q380 240 250 410',
  'M-20 720 Q130 560 250 410',
  'M250 920 Q235 660 250 410',
];

/** Lock fill opacity — slightly stronger than scattered pins so the motif reads. */
const LOCK_FILL_OPACITY_DARK = 0.1;
const LOCK_FILL_OPACITY_LIGHT = 0.16;
const RING_OPACITY_DARK = 0.08;
const RING_OPACITY_LIGHT = 0.12;

export const ShareLockIllustration = memo(function ShareLockIllustration() {
  const {
    isDark,
    bgColor,
    gridColor,
    gridWidth,
    accent,
    dashColor,
    glowStart,
    glowEnd,
    roadOp,
    prefersReducedMotion,
  } = useMapIllustrationTheme();

  const lockFill = isDark ? LOCK_FILL_OPACITY_DARK : LOCK_FILL_OPACITY_LIGHT;
  const ringOpacity = isDark ? RING_OPACITY_DARK : RING_OPACITY_LIGHT;

  return (
    <svg
      viewBox="0 0 500 900"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      className={styles.illustration}
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="lockGrid"
          width="50"
          height="50"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 50 0 L 0 0 0 50"
            fill="none"
            stroke={gridColor}
            strokeWidth={gridWidth}
          />
        </pattern>
        {!isDark && (
          <radialGradient id="lockGlow" cx="50%" cy="46%" r="55%">
            <stop offset="0%" stopColor={glowStart} />
            <stop offset="100%" stopColor={glowEnd} />
          </radialGradient>
        )}
      </defs>

      <rect
        width="500"
        height="900"
        fill={isDark ? bgColor : 'url(#lockGlow)'}
      />
      <rect width="500" height="900" fill="url(#lockGrid)" />

      {/* Converging roads */}
      {CONVERGING_ROADS.map((road) => (
        <g key={road}>
          <path
            d={road}
            stroke={accent}
            strokeWidth="26"
            opacity={roadOp.wide}
            strokeLinecap="round"
          />
          <path
            d={road}
            stroke={accent}
            strokeWidth="1.5"
            opacity={roadOp.edge}
            strokeLinecap="round"
          />
          <path
            d={road}
            stroke={dashColor}
            strokeWidth="1.5"
            strokeDasharray="8 14"
            opacity={roadOp.dash}
            strokeLinecap="round"
          />
        </g>
      ))}

      {/* Security perimeter rings */}
      {SECURITY_RINGS.map((ring) => (
        <circle
          key={ring.radius}
          cx={CENTER.x}
          cy={CENTER.y}
          r={ring.radius}
          stroke={accent}
          strokeWidth="1.5"
          opacity={ringOpacity * ring.opacityFactor}
        />
      ))}

      {/* Pulsing outer ring */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={INNER_RING_RADIUS}
        stroke={accent}
        strokeWidth="2"
        opacity={ringOpacity * 1.5}
      >
        {!prefersReducedMotion && (
          <>
            <animate
              attributeName="r"
              values={`${INNER_RING_RADIUS};${PULSE_MAX_RADIUS};${INNER_RING_RADIUS}`}
              dur="5s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values={`${ringOpacity * 1.6};0;${ringOpacity * 1.6}`}
              dur="5s"
              repeatCount="indefinite"
            />
          </>
        )}
      </circle>

      {/* Lock pin face */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={PIN_RADIUS}
        fill={accent}
        opacity={lockFill}
      />
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={PIN_RADIUS}
        stroke={accent}
        strokeWidth="2"
        opacity={lockFill * 2.5}
      />

      {/* Keyhole cut */}
      <circle
        cx={CENTER.x}
        cy={KEYHOLE_CIRCLE_CY}
        r={KEYHOLE_CIRCLE_RADIUS}
        fill={accent}
        opacity={lockFill * 3}
      />
      <path d={KEYHOLE_STEM} fill={accent} opacity={lockFill * 3} />
    </svg>
  );
});
